/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Image } from '@components/atoms'

import { useAppSelector, CartType } from '@redux/index'
import { grey } from '@mui/material/colors'
import { Decimal } from '@utils'

import kedem6Image from '@public/images/pages/shop/kedem6/thumbnail/kedem6-thumb2.jpg'
import kedem4Image from '@public/images/pages/shop/kedem4/thumbnail/kedem4-thumb2.jpg'
import amp2Image from '@public/images/pages/shop/amp2/thumbnail/amp2-thumb2.jpg'
import dsImage from '@public/images/pages/shop/dualSwitch/gallery/ds-gallery2.jpg'
import noImage from '@public/images/pages/shop/noimage.jpg'

const imageObj = {
	KDM6: kedem6Image,
	KDM4: kedem4Image,
	AMP2: amp2Image,
	DS: dsImage,
}

export const PaymentItems = () => {
	// redux state
	const cartList: CartType[] = useAppSelector(state => state.payment.tryToPayment)[
		'cartList'
	]

	const vatSubtotal: {
		usd: number
		qty: number
	} = React.useMemo(() => {
		const res: {
			usd: number
			qty: number
		} = cartList.reduce(
			(sum, { sumUsd, vatUsd, qty }) => {
				return {
					usd: Decimal(sum.usd + sumUsd + vatUsd),
					qty: sum.qty + qty,
				}
			},
			{
				usd: 0,
				qty: 0,
			},
		)
		return res
	}, [cartList])

	return (
		<View css={style}>
			<View className="infoTitleView">
				<Text category="h3">Items</Text>
			</View>

			<View className="orderItemsView">
				{cartList.map(anItem => {
					const imageKey = anItem.productCode as keyof typeof imageObj
					const image = imageKey in imageObj ? imageObj[imageKey] : noImage

					return (
						<View key={anItem.id} className="cartItemView">
							<View className="itemView">
								<View className="itemImageView">
									<Image
										src={image}
										className="itemImage"
										onLoad={event => {
											const imgElement = event.target as HTMLImageElement
											const isPortrait =
												imgElement.naturalHeight > imgElement.naturalWidth
											if (isPortrait) {
												imgElement.style.width = 'auto'
												imgElement.style.height = '100%'
											} else {
												imgElement.style.width = '100%'
												imgElement.style.height = 'auto'
											}
										}}
									/>
								</View>

								<View className="itemNameView">
									<View className="itemNameTextView">
										<Text className="itemName" category="h5">
											{anItem.productName}
										</Text>
										{anItem.option?.id !== '' && (
											<Text category="p2" hint="500">
												{anItem.option?.name}
											</Text>
										)}
									</View>

									<Text category="p1" className="itemComplex">
										x
									</Text>

									<Text category="p1">{anItem.qty}</Text>
								</View>
							</View>

							<View className="itemDetailView usd">
								<Text category="h6">
									{Decimal(anItem.sumUsd + anItem.vatUsd).toLocaleString('en-US', {
										style: 'currency',
										currency: 'USD',
									})}
								</Text>
							</View>
						</View>
					)
				})}

				<View className="subtotalOuterView">
					<View className="subtotalView">
						<Text category="s1" className="subtotalLabel">
							{`Subtotal x ${vatSubtotal.qty} Items :`}
						</Text>
						<Text category="h3" className="subtotalValue">
							{vatSubtotal.usd.toLocaleString('en-US', {
								style: 'currency',
								currency: 'USD',
							})}
						</Text>
					</View>
					{/* <Text category="p1" hint="500" className="vatLabel">
						(VAT Included)
					</Text> */}
				</View>
			</View>
		</View>
	)
}

const style = css({
	marginBottom: '3rem',
	background: grey[200],

	'.infoTitleView': {
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center',
		paddingLeft: '1rem',
		width: '100%',
		height: '4rem',
		borderBottom: '1px solid',
		borderColor: grey[500],
	},

	'.orderItemsView': {
		padding: '1rem',
		background: grey[200],

		'.cartItemView': {
			display: 'flex',
			width: '100%',
			maxWidth: '40rem',
			height: '6rem',
			margin: 'auto',
			marginBottom: '1rem',

			'.itemView': {
				display: 'flex',
				justifyContent: 'flex-start',
				alignItems: 'center',
				width: '100%',

				'.itemImageView': {
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					overflow: 'hidden',
					width: '9rem',
					height: '6rem',
					borderRadius: '5px',
					border: '1px solid',
					borderColor: grey[500],

					'.itemImage': {
						width: '100%',
						heght: 'auto',
						objectFit: 'contain',
					},
				},

				'.itemNameView': {
					display: 'flex',
					justifyContent: 'flex-start',
					alignItems: 'center',
					marginLeft: '1rem',

					'.itemNameTextView': {
						textAlign: 'left',
					},

					'.itemComplex': {
						width: '2rem',
					},
				},
			},

			'.itemDetailView': {
				display: 'flex',
				justifyContent: 'flex-end',
				alignItems: 'center',
				width: '15rem',
				paddingRight: '2rem',
				textAlign: 'right',
			},
		},

		'.subtotalOuterView': {
			width: '100%',
			maxWidth: '40rem',
			margin: 'auto',
			paddingRight: '1rem',
			textAlign: 'right',
			borderTop: '1px solid',
			'.subtotalView': {
				display: 'flex',
				justifyContent: 'flex-end',
				alignItems: 'center',
				marginTop: '1rem',
				textAlign: 'left',

				'.subtotalLabel': {
					marginRight: '1rem',
					textAlign: 'left',
					marginTop: 0,
					marginBottom: 0,
				},

				'.subtotalValue': {
					marginTop: 0,
					marginBottom: 0,
				},
			},

			'.vatLabel': {
				marginTop: '0.3rem',
				marginBottom: '2rem',
			},
		},
	},

	'@media (max-width: 530px)': {
		'.orderItemsView': {
			'.cartItemView': {
				display: 'block',
				height: 'fit-content',
				'.itemView': {
					display: 'block',
					'.itemImageView': {
						margin: 'auto',
					},
					'.itemNameView': {
						justifyContent: 'center',

						p: {
							margin: '0.5rem 0',
						},
					},
				},
			},
		},
	},
})
